// The STG landing: a default-populated dashboard (KPI row + hero trend) rendered
// above the build-your-own grid. Detected by a fixed id so no backend schema
// change is needed and the user can still pin/build/remove cards on it.
//
// Everything here is UNIT-AGNOSTIC: the KPI/hero read `unit` from the API
// responses (today "förpackningar"), so the same views flip to dosor unchanged
// once the dos-ETL (MP-OS-9) lands.

import { flagUnauthorized } from './api/auth'
import { apiFetch } from './api/http'
import { formatRange } from './format'
import { BRAND_NOT_IDENTIFIED, isBrandMissing, type KitContents, type PackVariant } from './missingLabels'
import { TENANT } from './tenant'
import { COVERAGE_KEYS, isIncompletePoint, missingDays } from './charts/partial'
import type { Chart, ChartDatum, ShareBasis, Spec } from './types'

const API_BASE = import.meta.env.VITE_API_URL ?? ''

export const STG_LANDING_ID = 'stg-landing'
const STG_MANUFACTURER = TENANT.manufacturer // aliased server-side

/** The Sales Trends selection the articles export follows (MP-OS-210): the same parameter names
 * the trend requests send to /dos-daily; `brand` is the per-brand selection, when on. */
export interface ArticlesExportFilters {
  segment?: string
  brand?: readonly string[]
  strength?: string
  fmt?: string
  category?: string
  flavour?: string
  nicotine?: string
  product_filters_v2?: boolean
}

/** Download permitted market articles for the selected interval, supplier view and the
 * selection on screen. `filename` comes from buildExportFilename (MP-OS-190); an aborted
 * `signal` (the button's useDownload unmounted) cancels the request and never downloads a file. */
export async function downloadArticlesExport(from: string, to: string, filename: string,
  signal?: AbortSignal, filters: ArticlesExportFilters = {}): Promise<void> {
  const q = new URLSearchParams({ date_from: from, date_to: to })
  if (TENANT.manufacturer) q.set('manufacturer', TENANT.manufacturer)
  const { brand, ...rest } = filters
  for (const b of brand ?? []) q.append('brand', b)
  for (const [key, value] of Object.entries(rest)) if (value !== undefined && value !== '') q.set(key, String(value))
  const response = await apiFetch(`${API_BASE}/api/dashboard/articles-export?${q}`, { signal })
  if (response.status === 401) flagUnauthorized()
  if (!response.ok) throw new Error(`Article export failed (${response.status})`)
  const blob = await response.blob()
  if (signal?.aborted) return
  const url = URL.createObjectURL(blob)
  try {
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    a.click()
  } finally {
    URL.revokeObjectURL(url)
  }
}

export interface Summary {
  from: string
  to: string
  unit: string
  forpackningar: number
  orders: number
  // Internal-only (MP-OS-106): the API omits these for a manufacturer seat, so they are
  // optional here. Nothing in the customer-facing UI may render them.
  revenue_gross?: number
  revenue_net?: number
  customers: number
  forpackningar_per_customer: number | null
  scoped: boolean // true = this supplier's own figures; false = whole market
  source?: string
  data_as_of?: string // actual Heart observation time, not the selected reporting month
}

export function fetchSummary(from: string, to: string): Promise<Summary | null> {
  // The summary hits the cube (customer-metrics, which can hang) — bound it so a slow
  // backend resolves to null (→ "ej tillgänglig") instead of an endless loading state.
  const q = new URLSearchParams({ date_from: from, date_to: to })
  if (TENANT.manufacturer) q.set('manufacturer', TENANT.manufacturer)
  const url = `${API_BASE}/api/dashboard/summary?${q}`
  return dedupe(url, async () => {
    const ctrl = new AbortController()
    // The server has a 25s total budget. Do not abort a valid cold Heart query
    // at 12s; following loads read the shared daily observation from Postgres.
    const timer = setTimeout(() => ctrl.abort(), 30000)
    try {
      const r = await apiFetch(url, { signal: ctrl.signal })
      if (r.status === 401) flagUnauthorized()
      if (!r.ok) return null
      return (await r.json()) as Summary
    } catch {
      return null
    } finally {
      clearTimeout(timer)
    }
  })
}

// Validated DOSOR aggregates (May, "sold = ex-cancelled"). Totals/composition
// only — no daily series (needs the dated nightly ETL).
// MPD-190: a month's CU against Olof's monthly report, within the reconcile's ±5 % or not.
export type FacitStatus = 'matches' | 'differs' | 'none'

export interface DosLayer {
  unit: string
  month: string // 'YYYY-MM' the snapshot is for
  validated: boolean // facit_status === 'matches'
  facit_delta_pct: number | null // signed Δ% vs Olof's monthly report (whenever there is one)
  facit_status?: FacitStatus // MPD-190; absent from an older API (facitBadge falls back on validated)
  facit_tolerance_pct?: number // the ±% a match allows (5)
  validation: { own_dosor: number; facit_own_dosor: number | null; residual_note: string }
  // MPD-73: a supplier gets null for the whole-market total and its share of it (every segment);
  // the web never shows either.
  market: { dosor: number; forpackningar: number } | null
  own: { dosor: number; forpackningar: number; market_share_dosor: number | null }
  segment_dosor: Record<string, number>
  // Market CU per segment, NEVER the supplier's segment volume. For a supplier only its permitted
  // segments (and product types for Vape/Smoking) are present (MPD-73).
  market_segment_dosor?: Record<string, number>
  own_all_white_share_pct?: number | null // the supplier's All White share; null when All White is not permitted
  own_all_white_share_pct_alltime?: number | null // all-time All White share ("någonsin", MP-OS-77)
  segment_label?: string // what the share is "andel av" — "All White" or "marknaden"
  // resolved: false = the bucket of sales without a brand in Heart (labelled by fetchDosLayer).
  // own: the server's tag for the supplier's own brand (MPD-73); absent from an older API.
  // [] for a supplier whose permitted segments do not include All White.
  all_white_brands_dosor: BrandRankingRow[]
  // The largest resolved competitor brand over the WHOLE ranking, not only the rows above (MPD-73).
  // null = there is none; absent from an older API.
  all_white_largest_competitor?: CompetitorBrand | null
  // MPD-68: the supplier's MAIN segment (its largest by CU in the month, within its permitted
  // segments) and that segment's brand ranking. null = no sales in any permitted segment, or the
  // whole market. Absent from an older API — mainSegmentOf falls back to All White then.
  own_main_segment?: MainSegment | null
  main_segment_brands?: BrandRankingRow[]
  main_segment_largest_competitor?: CompetitorBrand | null
  // #116 — biggest brand across ALL segments; the admin whole market only (null for a supplier).
  largest_brand_overall?: { brand: string; share_of_market: number } | null

  // kit: a sold vape kit (MP-OS-150), counted as one unit.
  own_top_skus_dosor: { sku: string; brand: string; dosor: number; forp: number | null; namn_sv?: string; kit?: boolean; kit_contents?: KitContents | null; strength_label?: string | null; pack_variant?: PackVariant | null }[]
  own_all_skus?: { sku: string; brand: string; dosor: number; namn_sv?: string; kit?: boolean; kit_contents?: KitContents | null; strength_label?: string | null; pack_variant?: PackVariant | null }[] // full article list for the per-article selector (MP-OS-63)
}

export interface BrandRankingRow {
  brand: string
  dosor: number
  share_of_segment: number
  resolved?: boolean
  own?: boolean
}

export interface CompetitorBrand {
  brand: string
  dosor: number
  share_of_segment: number
}

export interface MainSegment {
  segment: string // the API's canonical label: 'All White', 'Tobacco', 'Nicotine Free', 'Vape', 'Smoking', 'Other'
  share_pct: number | null // null = no own sales in the month (never a stated 0 %)
  share_pct_alltime: number | null
  chosen_by: 'month' | 'alltime' // 'alltime' when the supplier sold nothing in the month
  market_dosor: number | null // the segment's CU in the month, within the supplier's segments; null = unknown
}

/** The supplier's main segment and its brand ranking. An API without the MPD-68 fields means
 *  the All White card it always had, so an older backend keeps working unchanged. */
export function mainSegmentOf(dos: DosLayer): {
  main: MainSegment | null
  brands: BrandRankingRow[]
  // The server's largest competitor; undefined = an older API that does not send it.
  competitor: CompetitorBrand | null | undefined
} {
  if (dos.own_main_segment !== undefined) {
    return { main: dos.own_main_segment, brands: dos.main_segment_brands ?? [], competitor: dos.main_segment_largest_competitor }
  }
  return {
    main: {
      segment: 'All White',
      share_pct: dos.own_all_white_share_pct ?? null,
      share_pct_alltime: dos.own_all_white_share_pct_alltime ?? null,
      chosen_by: 'month',
      market_dosor: dos.market_segment_dosor?.['All White'] ?? null,
    },
    brands: dos.all_white_brands_dosor,
    competitor: dos.all_white_largest_competitor,
  }
}

// MPD-68 review: sales without an identified brand are one bucket in a brand ranking. The API flags
// it resolved=false (an older API only names it "(oresolvat)"); this is the ONE place that
// recognises it in the dos-layer rankings. Its label is the shared one (MP-OS-197, missingLabels).
export const UNRESOLVED_BRAND_LABEL = BRAND_NOT_IDENTIFIED

function brandRows(rows: BrandRankingRow[]): BrandRankingRow[] {
  return rows.map((x) => x.resolved === false || isBrandMissing(x.brand)
    ? { ...x, brand: UNRESOLVED_BRAND_LABEL, resolved: false, ...(x.own !== undefined ? { own: false } : {}) }
    : { ...x, brand: decodeEntities(x.brand), resolved: true })
}

// One render asks for the same dos-layer snapshot from several places (the landing KPIs
// and the market cards each compose independently), which fired identical requests in
// parallel. Share the one in flight; the entry is dropped as soon as it settles, so the
// next render still refetches and nothing is cached across a period change.
const _inflight = new Map<string, Promise<unknown>>()

function dedupe<T>(key: string, run: () => Promise<T>): Promise<T> {
  const hit = _inflight.get(key) as Promise<T> | undefined
  if (hit) return hit
  const p = run().finally(() => _inflight.delete(key))
  _inflight.set(key, p)
  return p
}

// Throws on a real failure (network / 5xx) so callers can show an ERROR state; returns
// null only for a genuine 404 (no dos-layer for this scope) → a true empty state. The
// two must never be conflated (DD): a failure must not read as "no data".
export function fetchDosLayer(month?: string): Promise<DosLayer | null> {
  const q = `manufacturer=${encodeURIComponent(TENANT.manufacturer)}${month ? `&month=${month}` : ''}`
  const url = `${API_BASE}/api/dashboard/dos-layer?${q}`
  return dedupe(url, async () => {
    const r = await apiFetch(url)
    if (r.status === 401) flagUnauthorized()
    if (r.status === 404) return null
    if (!r.ok) throw new Error(`dos-layer HTTP ${r.status}`)
    const d = (await r.json()) as DosLayer
    return {
      ...d,
      all_white_brands_dosor: brandRows(d.all_white_brands_dosor),
      main_segment_brands: d.main_segment_brands && brandRows(d.main_segment_brands),
      all_white_largest_competitor: d.all_white_largest_competitor
        && { ...d.all_white_largest_competitor, brand: decodeEntities(d.all_white_largest_competitor.brand) },
      main_segment_largest_competitor: d.main_segment_largest_competitor
        && { ...d.main_segment_largest_competitor, brand: decodeEntities(d.main_segment_largest_competitor.brand) },
      largest_brand_overall: d.largest_brand_overall
        ? { ...d.largest_brand_overall, brand: decodeEntities(d.largest_brand_overall.brand) }
        : d.largest_brand_overall,
      own_top_skus_dosor: d.own_top_skus_dosor.map((x) => ({ ...x, brand: decodeEntities(x.brand),
        namn_sv: x.namn_sv ? decodeEntities(x.namn_sv) : x.namn_sv })),
      own_all_skus: d.own_all_skus?.map((x) => ({ ...x, brand: decodeEntities(x.brand),
        namn_sv: x.namn_sv ? decodeEntities(x.namn_sv) : x.namn_sv })),
    }
  })
}

// MP-OS-175 QA-10: the pack type's bucket; "size" rows carry their size in doses.
export type PackBucket = 'size' | 'diy_bag' | 'mega_can' | 'special' | 'special_custom' | 'unknown'

export interface PackRow {
  pack: string
  packages: number // sold, >= 0 (MP-OS-175 QA-09: returns are their own line)
  doses: number
  pct_of_packages: number
  pct_of_doses: number
  bucket?: PackBucket
  size?: number | null
}

// MP-OS-175 QA-09: the period's returns, all <= 0, split by whether the pack type is verified.
export interface PackReturns {
  packages: number
  doses: number
  verified_packages: number
  verified_doses: number
  unknown_packages: number
  unknown_doses: number
}

export interface MixpackRow {
  sku: string
  name: string | null
  brand: string | null
  components: [string, number][]
  packages: number
  doses: number
  kit?: boolean // MP-OS-150: a sold vape kit (device + pods, contents unknown), one unit per kit
  kit_contents?: KitContents | null // MPD-135: the kit's parts and contents line, display only
}

export type UnknownReason = 'missing_details' | 'mismatched_details' | 'missing_source_cans' | 'aggregate_counts' | 'bundle_or_conversion'
export type UnknownCause = 'pack_details' | 'aggregate_counts' | 'missing_source_cans' | 'bundle_or_conversion'

export interface UnknownSummary {
  packages: number
  doses: number
  absolute_packages: number
  absolute_doses: number
  articles?: number
  article_days?: number
  days?: number
  first_date?: string | null
  last_date?: string | null
}

export interface PackagesResponse {
  unit: string
  month: string
  brand: string | null
  brands: string[]
  sku?: string | null
  skus?: { sku: string; name: string | null; brand: string; kit?: boolean; kit_contents?: KitContents | null; pack_variant?: PackVariant | null
    strength_label?: string | null }[] // MPD-135: a vape article's strength as labelled
  rows: PackRow[]
  totals: { packages: number; doses: number } // net: sold + returns
  sold?: { packages: number; doses: number } // MP-OS-175 QA-09: sum of rows; optional for an older API
  returns?: PackReturns
  mixpacks: MixpackRow[]
  caveat?: string | null
  pack_sizes_verified?: boolean
  pack_size_status?: 'empty' | 'complete' | 'partial' | 'unavailable'
  coverage?: {
    verified_articles: number
    total_articles: number
    partially_verified_articles?: number
    unknown_reasons?: (UnknownSummary & { reason: UnknownReason })[]
    // MP-OS-175: the reasons grouped by who can resolve them (our load, Heart, mixpacks).
    unknown_causes?: (UnknownSummary & { cause: UnknownCause; reasons: UnknownReason[] })[]
  }
  source?: string
}

// Pack-size table + mixpack breakdown (B4 / MP-OS-103) — the table their monthly report
// already ships. `brand` narrows it, which makes the percentages read "within that brand".
export async function fetchPackages(month?: string, brand?: string, sku?: string, area?: string): Promise<PackagesResponse | null> {
  const q = new URLSearchParams()
  if (TENANT.manufacturer) q.set('manufacturer', TENANT.manufacturer)
  if (month) q.set('month', month)
  if (brand) q.set('brand', brand)
  if (sku) q.set('sku', sku)
  if (area) q.set('area', area)
  const r = await apiFetch(`${API_BASE}/api/dashboard/packages?${q.toString()}`)
  if (r.status === 401) flagUnauthorized()
  if (r.status === 404) return null
  if (!r.ok) throw new Error(`packages HTTP ${r.status}`)
  const d = (await r.json()) as PackagesResponse
  return {
    ...d,
    brands: (d.brands ?? []).map(decodeEntities),
    skus: d.skus?.map((s) => ({ ...s, brand: decodeEntities(s.brand),
      name: s.name ? decodeEntities(s.name) : s.name })),
  }
}

// U4: some brands were stored HTML-escaped by an older ETL (sku_meta.brand = "L&amp;M",
// "Nick &amp; Johnny") and the nightly never re-resolves an already-resolved brand, so they
// won't self-heal. Decode at the fetch boundary — display AND the filter keys derived from
// brand stay consistent. Decode &amp; last so "&amp;lt;" resolves correctly.
export function decodeEntities(s: string): string {
  return s && s.indexOf('&') !== -1
    ? s
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#0?39;|&apos;/g, "'")
        .replace(/&amp;/g, '&')
    : s
}

export interface BrandOption {
  brand: string
  producer: string | null
  dosor: number
  own: boolean
}

// Every brand in the market, biggest first, each marked own/competitor — the choice list
// for the brand comparison (MP-OS-98). Brand-level aggregates only.
export async function fetchBrands(segment?: string): Promise<BrandOption[]> {
  const q = new URLSearchParams()
  if (TENANT.manufacturer) q.set('manufacturer', TENANT.manufacturer)
  if (segment) q.set('segment', segment)
  const r = await apiFetch(`${API_BASE}/api/dashboard/brands?${q}`)
  if (r.status === 401) flagUnauthorized()
  if (!r.ok) throw new Error('Could not load brands.')
  return ((await r.json()).brands ?? []).map((b: BrandOption) => ({
    ...b, brand: decodeEntities(b.brand), producer: b.producer ? decodeEntities(b.producer) : b.producer,
  })) as BrandOption[]
}

export interface SkuRow {
  ownership?: 'own' | 'mixed' | 'other' | 'unknown' | null
  sku: string
  namn_sv: string | null
  // null on the brand list's remainder row (MP-OS-197): the articles without an identified brand.
  brand: string | null
  unbranded?: boolean // brand rows only: true on the one remainder row
  dosor: number
  prev_dosor: number
  change_pct: number | null
  conversion: string | null // DIY/Mega recount note (e.g. "× 25 dosor per påse"), else null
  rank: number // 1-based placement this month (MP-OS-79)
  prev_rank: number | null // placement the previous month, null if new (MP-OS-80)
  share_pct: number // this article's share of the shown total (MP-OS-82)
  segment: string // normalised segment — drives the segment selector (MP-OS-103)
  sku_count?: number // brand rows only (by=brand): how many articles sit behind the brand
  kit?: boolean // MP-OS-150: a sold vape kit (contents unknown), counted as one unit
  kit_contents?: KitContents | null // MPD-135: the kit's parts and contents line, display only
  strength_label?: string | null // MPD-135: a vape article's strength as labelled (the API sends null for an mg/ml label, MPD-141)
  pack_variant?: PackVariant | null // MP-OS-209: a verified pack variant, names same-name articles apart
}

// The full per-SKU dosor list for the active supplier (latest month + change vs the
// previous month) — the trendable "every SKU" list (MP-OS-49). Scoped to TENANT.
export type SkuListGran = 'week' | 'month' | 'year'

export interface SkuListResponse {
  month: string
  prev: string
  granularity: SkuListGran
  label: string // human window label (a month, or a from–to range for week/year)
  prev_label: string | null
  comparison_available?: boolean
  ongoing?: boolean
  through?: string | null
  date_from?: string // actual source window, including an ongoing partial month
  date_to?: string
  rows: SkuRow[]
  share_basis?: ShareBasis // what the Share column divides by (MP-OS-187)
  excluded_mixpacks?: { count: number } // MPD-185: mix packs without CU left out of the list
}

export async function fetchSkuList(
  market = false,
  granularity: SkuListGran = 'month',
  month?: string,
  segment?: string,
  by: 'sku' | 'brand' = 'sku',
  area?: string,
  week?: string,
  leaf?: string,
): Promise<SkuListResponse | null> {
  // market=true → the whole-market leaderboard across all brands (MP-OS-84); else scoped
  // to the active supplier. Competitor mode preserves the assortment scope.
  // `month` is the global period (A4/C15): without it the backend fell back to its freshest
  // month, so the ranking read July while the rest of the app showed June.
  const params = new URLSearchParams({ granularity })
  if (month) params.set('month', month)
  if (segment) params.set('segment', segment)
  if (area) params.set('area', area)
  if (leaf) params.set('leaf', leaf)
  if (week) params.set('week', week)
  if (by !== 'sku') params.set('by', by)
  if (market) params.set('market', 'true')
  if (TENANT.manufacturer) params.set('manufacturer', TENANT.manufacturer)
  const r = await apiFetch(`${API_BASE}/api/dashboard/sku-list?${params.toString()}`)
  if (r.status === 401) flagUnauthorized()
  if (r.status === 404) return null
  if (!r.ok) throw new Error(`sku-list HTTP ${r.status}`)
  const d = (await r.json()) as SkuListResponse
  return { ...d, rows: d.rows.map((row) => ({ ...row, brand: row.brand == null ? null : decodeEntities(row.brand),
    namn_sv: row.namn_sv ? decodeEntities(row.namn_sv) : row.namn_sv })) }
}

export interface MeasureDef {
  id: string
  label: string
  definition: string
  unit: string
  validation: string
  facit_delta_pct: number | null
  provenance: string
}

// The certified-measure registry — the single source of truth for the one-sentence
// definition + provenance shown on every figure. Certified only by default.
export async function fetchMeasures(): Promise<Record<string, MeasureDef>> {
  try {
    const r = await apiFetch(`${API_BASE}/api/measures`)
    if (!r.ok) return {}
    const data = (await r.json()) as { measures: MeasureDef[] }
    return Object.fromEntries(data.measures.map((m) => [m.id, m]))
  } catch {
    return {}
  }
}

export interface SegmentShare {
  segment: string
  own_dosor: number
  market_dosor: number
  market_share_pct: number
}

// Market share per segment (Tobacco / All White / Nicotine Free, + Vape) — Olof's
// prio-1 KPI. Own sold cans ÷ the whole market's in that segment, from the lager
// (sold = completed). Reconciles to the Vinnarmatchen facit ±~4 %.
export async function fetchSegments(month?: string): Promise<{ month: string; segments: SegmentShare[] } | null> {
  const q = `manufacturer=${encodeURIComponent(TENANT.manufacturer)}${month ? `&month=${month}` : ''}`
  const r = await apiFetch(`${API_BASE}/api/dashboard/segments?${q}`)
  if (r.status === 401) flagUnauthorized()
  if (r.status === 404) return null
  if (!r.ok) throw new Error(`segments HTTP ${r.status}`)
  return (await r.json()) as { month: string; segments: SegmentShare[] }
}

// Segment-aware standard view (Olof's KPI taxonomy): per category the supplier's own
// volume + market share per leaf segment, only the categories/segments it actually has.
export interface SegmentLeaf {
  segment: string
  own: number
  market: number
  share_pct: number
}
export interface SegmentCategory {
  category: string
  unit: string // 'dosor' (snus) | 'units' (vape/smoking)
  own_total: number
  market_total: number
  share_total_pct: number
  whole_market?: boolean // the admin whole market (no supplier scope); absent from an older API
  segments: SegmentLeaf[]
}
export async function fetchSegmentView(month?: string, granularity: SkuListGran = 'month', week?: string): Promise<SegmentCategory[]> {
  const q = `manufacturer=${encodeURIComponent(TENANT.manufacturer)}${month ? `&month=${encodeURIComponent(month)}` : ''}`
    + (granularity !== 'month' ? `&granularity=${granularity}` : '')
    + (granularity === 'week' && week ? `&week=${encodeURIComponent(week)}` : '')
  const r = await apiFetch(`${API_BASE}/api/dashboard/segment-view?${q}`)
  if (r.status === 401) flagUnauthorized()
  if (r.status === 404) return []
  if (!r.ok) throw new Error(`segment-view HTTP ${r.status}`)
  return ((await r.json()).categories ?? []) as SegmentCategory[]
}

// Admin god-view: every manufacturer in the data (onboarded ones named officially, the
// rest derived from top brand). `value` is passed as ?manufacturer= (code or numeric id).
export interface ManufacturerOption {
  value: string
  label: string
  dosor: number
  onboarded: boolean
}
export async function fetchManufacturers(): Promise<ManufacturerOption[]> {
  try {
    const r = await apiFetch(`${API_BASE}/api/manufacturers`)
    if (r.status === 401) flagUnauthorized()
    if (!r.ok) return [] // 403 for a supplier seat → caller falls back to its own only
    return ((await r.json()).manufacturers ?? []) as ManufacturerOption[]
  } catch {
    return []
  }
}

export type Freq = 'day' | 'week' | 'month'
export const FREQ_LABEL: Record<Freq, string> = { day: 'Day', week: 'Week', month: 'Month' }

// Rolling-mean window per granularity. Olof's reasoning: 7 days = a whole week, 4 weeks
// ≈ a month — the payday spike is exactly what he smooths away. Month = a quarter.
export const ROLLING_WINDOW: Record<Freq, number> = { day: 7, week: 4, month: 3 }

// Every plotted series key in a chart. Single-series charts from the API key their
// points on `value`; merged multi-series charts (per brand / compare) key on the brand
// name. Reading the keys off the data covers both, so a transform can never silently
// skip a mode.
export function seriesKeys(chart: Chart): string[] {
  const metadata = new Set(['name', 'period', 'partial', 'period_from', 'period_to', 'period_kind', ...COVERAGE_KEYS])
  if (chart.series?.length) return chart.series.filter((key) => !metadata.has(key))
  const keys = new Set<string>()
  chart.data.forEach((d) => {
    Object.keys(d).forEach((k) => {
      if (!metadata.has(k)) keys.add(k)
    })
  })
  return [...keys]
}

const measurement = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null

// Rebase every series to its own first non-zero point = 100, so a small brand plotted
// against a large one is still readable. Without this the small series flattens against a
// y-axis the big one owns — Olof hit exactly that: "det blir bara ett rakt streck
// eftersom skalan blir…". Indexing compares SHAPE (growth), not level, so the axis unit
// changes from dosor to an index; callers must relabel.
// A series that is zero throughout has no base to rebase on and is left alone rather than
// divided by zero.
export function indexSeries(chart: Chart): Chart {
  let data = chart.data.map((d) => ({ ...d }))
  const bases = new Set<number>()
  for (const key of seriesKeys(chart)) {
    const vals = data.map((d) => measurement(d[key]))
    const baseAt = vals.findIndex((v) => v !== null && v !== 0)
    if (baseAt < 0) continue
    const base = vals[baseAt]!
    bases.add(baseAt)
    data = data.map((d, i) => {
      const value = vals[i]
      return { ...d, [key]: value == null ? null : Math.round((value / base) * 1000) / 10 }
    })
  }
  // MPD-133 L7: an incomplete base bucket understates the base, so every indexed point is marked.
  return { ...chart, data: markDerivedIncomplete(chart.data, data, () => [...bases]), unit: 'index (start = 100)' }
}

// MPD-133 L7: a derived point (cumulative, rolling average, indexed) whose value includes an
// incomplete bucket is marked incomplete too (charts/partial.ts), with the missing days it includes.
// `includes(i)` lists the source rows point i is computed from. A row that is incomplete itself
// keeps its own marking.
function markDerivedIncomplete(source: ChartDatum[], data: ChartDatum[], includes: (i: number) => number[]): ChartDatum[] {
  if (!source.some(isIncompletePoint)) return data
  return data.map((d, i) => {
    if (isIncompletePoint(d)) return d
    const from = includes(i).filter((j) => j !== i && isIncompletePoint(source[j]))
    if (!from.length) return d
    return { ...d, coverage: 'incomplete', coverage_via: 'derived', missing_days: from.reduce((sum, j) => sum + missingDays(source[j]), 0) }
  })
}
const range = (from: number, to: number): number[] => Array.from({ length: to - from + 1 }, (_, k) => from + k)

// Rolling mean, then cumulation — pure arithmetic on grounded values, never fabricated
// points. Applied to EVERY series, so per-brand and compare modes transform too instead
// of rendering raw data under a caption that claims otherwise.
export function transformSeries(
  chart: Chart,
  opts: { acc: boolean; avg: boolean; window: number },
): Chart {
  if (!opts.acc && !opts.avg) return chart
  const w = Math.max(1, Math.floor(opts.window))
  let data = chart.data.map((d) => ({ ...d }))
  for (const key of seriesKeys(chart)) {
    let vals = data.map((d) => measurement(d[key]))
    if (opts.avg) {
      vals = vals.map((_, i) => {
        const win = vals.slice(Math.max(0, i - (w - 1)), i + 1)
        if (!win.every((v): v is number => v !== null)) return null
        return Math.round(win.reduce((s, x) => s + x, 0) / win.length)
      })
    }
    if (opts.acc) {
      let c: number | null = 0
      vals = vals.map((v) => {
        c = c === null || v === null ? null : c + v
        return c
      })
    }
    data = data.map((d, i) => ({ ...d, [key]: vals[i] }))
  }
  // A cumulative point includes every bucket up to it; a rolling average its window.
  return { ...chart, data: markDerivedIncomplete(chart.data, data, (i) => opts.acc ? range(0, i) : range(Math.max(0, i - (w - 1)), i)) }
}

// Sold DOSOR over time from the materialized dos-layer — the standard view's trend
// source (NOT the cube/förpackningar). Scope by supplier, brand (compare) or SKU (drill).
// All in dosor — the unit suppliers actually count.
export async function fetchDosTrend(opts: {
  from: string
  to: string
  freq: Freq
  manufacturer?: string
  brand?: string
  sku?: string
  segment?: string
  flavor?: string
  strength?: string
  fmt?: string
  category?: string
  flavour?: string
  nicotine?: string
  product_filters_v2?: boolean
}): Promise<Chart | null> {
  const q = new URLSearchParams({ date_from: opts.from, date_to: opts.to, granularity: opts.freq })
  if (opts.manufacturer !== undefined) q.set('manufacturer', opts.manufacturer)
  if (opts.brand) q.set('brand', opts.brand)
  if (opts.sku) q.set('sku', opts.sku)
  if (opts.segment) q.set('segment', opts.segment)
  if (opts.flavor) q.set('flavor', opts.flavor)
  if (opts.strength) q.set('strength', opts.strength)
  if (opts.fmt) q.set('fmt', opts.fmt)
  if (opts.category) q.set('category', opts.category)
  if (opts.flavour) q.set('flavour', opts.flavour)
  if (opts.nicotine) q.set('nicotine', opts.nicotine)
  if (opts.product_filters_v2) q.set('product_filters_v2', 'true')
  const r = await apiFetch(`${API_BASE}/api/dashboard/dos-daily?${q.toString()}`)
  if (r.status === 401) flagUnauthorized()
  if (r.status === 404) return null
  if (!r.ok) throw new Error(`dos-daily HTTP ${r.status}`)
  return ((await r.json()).chart ?? null) as Chart | null
}

// Marknadsandel över tid (MP-OS-73) — own dosor ÷ denominator dosor per bucket, the
// andel-curve that mirrors fetchDosTrend in shape and controls. `segment` narrows the
// denominator to All White / Tobak / Vape / Nikotinfritt; `brand` overlays a competitor
// brand's share instead of the supplier's own. Unit is % (see chart.unit).
export async function fetchMarketShareTrend(opts: {
  from: string
  to: string
  freq: Freq
  manufacturer?: string
  brand?: string
  segment?: string
  sku?: string
  flavor?: string
  strength?: string
  fmt?: string
  category?: string
  flavour?: string
  nicotine?: string
  product_filters_v2?: boolean
  // MPD-84 N2: under a supplier scope, the own curve reads against the permitted market too.
  permitted_market?: boolean
}): Promise<Chart | null> {
  const q = new URLSearchParams({ date_from: opts.from, date_to: opts.to, granularity: opts.freq })
  if (opts.manufacturer !== undefined) q.set('manufacturer', opts.manufacturer)
  if (opts.brand) q.set('brand', opts.brand)
  if (opts.segment) q.set('segment', opts.segment)
  if (opts.sku) q.set('sku', opts.sku)
  if (opts.flavor) q.set('flavor', opts.flavor)
  if (opts.strength) q.set('strength', opts.strength)
  if (opts.fmt) q.set('fmt', opts.fmt)
  if (opts.category) q.set('category', opts.category)
  if (opts.flavour) q.set('flavour', opts.flavour)
  if (opts.nicotine) q.set('nicotine', opts.nicotine)
  if (opts.product_filters_v2) q.set('product_filters_v2', 'true')
  if (opts.permitted_market) q.set('permitted_market', 'true')
  const r = await apiFetch(`${API_BASE}/api/dashboard/share-daily?${q.toString()}`)
  if (r.status === 401) flagUnauthorized()
  if (r.status === 404) return null
  if (!r.ok) throw new Error(`share-daily HTTP ${r.status}`)
  return ((await r.json()).chart ?? null) as Chart | null
}

export interface AttributeOption {
  value: string
  // MP-OS-191 #13: the English display label (filter-options); `value` is what is sent back.
  label?: string
  skus: number
  // MP-OS-201: with a period, CU sold in the curve's scope; an option without sales is only
  // returned while it is selected, marked `no_sales`.
  sold_cu?: number
  no_sales?: boolean
}

export type ProductFacet = 'brand' | 'category' | 'strength' | 'format' | 'flavour' | 'nicotine'
export interface ProductFilterOptions {
  fields: ProductFacet[]
  options: Partial<Record<ProductFacet, AttributeOption[]>>
  selected: Record<ProductFacet, string[]>
  unavailable: ProductFacet[]
  // MP-OS-201: CU sold for the whole selection in the period (only with a period).
  matching_sold_cu?: number
}
// Mirrors app/api/product_catalogue.py (MP-OS-167); filterLoading.test.ts fails if
// they drift. The server retries a failing catalogue lookup after each of
// RETRY_DELAYS until RETRY_BUDGET is spent, answers 503 with Retry-After for the
// rest of UNAVAILABLE_COOLDOWN, and the next demand after that starts a new budget.
export const CATALOGUE_RETRY_DELAYS_S = [2, 5, 10, 20, 30] as const
export const CATALOGUE_RETRY_BUDGET_S = 60
export const CATALOGUE_UNAVAILABLE_COOLDOWN_S = 60
// The longest Retry-After the server sends for filter options (202: at most 10 s;
// 503: the remaining cooldown).
const MAX_FILTER_POLL_S = CATALOGUE_UNAVAILABLE_COOLDOWN_S
// One budget's scheduled attempts end at the sum of the delays (2+5+10+20+30 = 67 s).
const BUDGET_SPAN_S = CATALOGUE_RETRY_DELAYS_S.reduce((sum, delay) => sum + delay, 0)
// Margin for one 202 poll interval (the server caps it at 10 s) plus request and
// lookup time, since each budget starts only on the first poll after the cooldown.
const FILTER_RECOVERY_MARGIN_S = 15
// A source that is back at any point of the second budget is picked up: first budget
// (67 s) + cooldown (60 s) + second budget (67 s) + margin (15 s) = 209 s.
export const FILTER_RECOVERY_BOUND_MS = (2 * BUDGET_SPAN_S + CATALOGUE_UNAVAILABLE_COOLDOWN_S + FILTER_RECOVERY_MARGIN_S) * 1000
// Past one budget the source is failing, not just slow: say so while still polling.
export const FILTER_SLOW_AFTER_MS = CATALOGUE_RETRY_BUDGET_S * 1000
// A request still in flight at the bound gets this long before it is abandoned.
const FILTER_REQUEST_GRACE_MS = 30_000

export async function fetchProductFilters(segment?: string, selection: Partial<Record<ProductFacet, string[]>> = {}, changed?: ProductFacet, signal?: AbortSignal, onSlow?: () => void, period?: { from: string; to: string }): Promise<ProductFilterOptions> {
  const q = new URLSearchParams()
  q.set('loading_status', 'true')
  if (segment) q.set('segment', segment)
  // MP-OS-201: the trend window, so only values with sales in it are offered.
  if (period) { q.set('date_from', period.from); q.set('date_to', period.to) }
  if (TENANT.manufacturer) q.set('manufacturer', TENANT.manufacturer)
  if (changed) q.set('changed', changed)
  for (const [key, values] of Object.entries(selection)) {
    for (const value of values ?? []) q.append(key === 'format' ? 'fmt' : key, value)
  }
  // Capture scope once; changing supplier/segment aborts this entire poll.
  // 202 is real background progress, never a usable partial facet response.
  const controller = new AbortController()
  const cancel = () => controller.abort(signal?.reason)
  signal?.addEventListener('abort', cancel, { once: true })
  if (signal?.aborted) cancel()
  const started = Date.now()
  let slow = false
  const reportSlow = () => { if (!slow) { slow = true; onSlow?.() } }
  const slowTimer = setTimeout(reportSlow, FILTER_SLOW_AFTER_MS)
  const deadline = setTimeout(() => controller.abort(new Error('Could not load product filters.')),
    FILTER_RECOVERY_BOUND_MS + FILTER_REQUEST_GRACE_MS)
  try {
    for (;;) {
      controller.signal.throwIfAborted()
      const r = await apiFetch(`${API_BASE}/api/dashboard/filter-options?${q}`, { signal: controller.signal })
      controller.signal.throwIfAborted()
      if (r.status === 401) flagUnauthorized()
      if (r.ok && r.status !== 202) return await r.json() as ProductFilterOptions
      if (r.status !== 202 && r.status !== 503) throw new Error('Could not load product filters.')
      // 503 means a server budget is spent; recovery waits for its cooldown.
      if (r.status === 503) reportSlow()
      const remaining = FILTER_RECOVERY_BOUND_MS - (Date.now() - started)
      if (remaining <= 0) throw new Error('Could not load product filters.')
      const fallback = r.status === 202 ? 2 : 15
      const seconds = Number(r.headers.get('Retry-After') ?? fallback)
      const wait = Number.isFinite(seconds) ? Math.min(MAX_FILTER_POLL_S, Math.max(1, seconds)) : fallback
      const delay = Math.min(wait * 1000, remaining)
      await new Promise<void>((resolve, reject) => {
        const abort = () => { clearTimeout(timer); reject(controller.signal.reason) }
        const timer = setTimeout(() => { controller.signal.removeEventListener('abort', abort); resolve() }, delay)
        controller.signal.addEventListener('abort', abort, { once: true })
        if (controller.signal.aborted) abort()
      })
    }
  } finally {
    clearTimeout(slowTimer)
    clearTimeout(deadline)
    signal?.removeEventListener('abort', cancel)
  }
}

// Retained for other consumers of the original attribute endpoint.
export async function fetchAttributes(segment?: string): Promise<{ strength: AttributeOption[]; format: AttributeOption[] }> {
  const q = new URLSearchParams()
  if (segment) q.set('segment', segment)
  const r = await apiFetch(`${API_BASE}/api/dashboard/attributes?${q}`)
  if (!r.ok) throw new Error('Could not load product filters.')
  return await r.json()
}

export interface Period {
  from: string
  to: string
  label: string
  value: string // 'YYYY-MM' — the month this period maps to (drives the dos snapshot)
  ongoing: boolean // an incomplete current month — don't compare it whole-vs-whole
  through: string | null // 'YYYY-MM-DD' last day with data, when ongoing
  settling: boolean // calendar-complete but its orders are still maturing to 'completed'
  // (settlement lag) — its figure is real-but-rising, so mark it, don't compare it as a dip
}

// Date range for the dosor time-series charts. At 'month' granularity a single-month
// period is ONE bucket (a lone dot, no curve), so widen to the full available month span
// (periods is latest-first) for a real month-over-month trend. Day/week keep the selected
// month. Pure so the behaviour is unit-tested (landing.test.ts).
// --- Dynamic trend window (Olof: "kan man styra längden på graferna?") ----------
// The time-series charts are no longer bound to the selected month; the user picks
// the window freely. A preset counts back N granularity-units ending at the latest
// data date; 'custom' is a verbatim from–to. The month pills still anchor the
// facit-certified KPI/scorecard month — only the trend window is free.
export type TrendWindow =
  | { kind: 'preset'; n: number }
  | { kind: 'ytd' } // "hittills i år" — Olof asked for this explicitly, not the full year
  | { kind: 'custom'; from: string; to: string }

// Quick spans per granularity (the unit IS the granularity). Each starts at the
// smallest span that still reads as a trend (≥4 points) and ends at the per-view
// MAX (Olof's rule): week → 52 v (one year; 64 v is unreadable), month → 24 mån
// (two years), day → 730 d (two years). usableWindowPresets caps the OFFERED spans
// to what the data can fill, so these grow toward the max as history deepens.
export const WINDOW_PRESETS: Record<Freq, number[]> = {
  day: [7, 14, 30, 90, 180, 365, 730],
  week: [4, 8, 12, 26, 52],
  month: [3, 6, 12, 24],
}

// Only offer spans the data can actually fill DIFFERENTLY. With ~4 months of data,
// 6/12/24-month spans all show the same 4 points (the empty lead is trimmed) and the
// buttons look broken. Keep presets up to and incl. the first that already covers all
// available units — so the longest button = "hela perioden", nothing redundant beyond.
// Grows automatically as the data deepens.
export function usableWindowPresets(freq: Freq, availableUnits: number): number[] {
  // Month view always offers the full two-year ladder so the 24-mo span (and default) exists
  // before 24 months of history accrue — the empty lead is trimmed (Olof, MP-OS-118).
  if (freq === 'month') return [...WINDOW_PRESETS.month]
  const out: number[] = []
  for (const n of WINDOW_PRESETS[freq]) {
    out.push(n)
    if (n >= availableUnits) break
  }
  return out
}
export const WINDOW_UNIT: Record<Freq, string> = { day: 'd', week: 'w', month: 'mo' }

// The default window snaps to the LONGEST span the data can currently fill (growing
// toward the per-view max as history deepens). Olof: the first glance should show
// the whole trend — no time-picker fiddling — so we default to max, not a short
// recent slice. The user can still narrow it (a shorter preset or a custom range).
export function defaultWindowN(freq: Freq, availableUnits: number): number {
  const usable = usableWindowPresets(freq, availableUnits)
  const max = usable[usable.length - 1]
  // Day opens at 90 d, not the full span — a year of daily points is an unreadable first
  // glance (Olof 4/9: "dag ska inte vara sådär lång i sin startvy, jag skulle säga nittio";
  // 365/730 d stay selectable). Week/month still open at their max (52 w / 24 mo). Capped
  // to what the data can fill.
  if (freq === 'day') return Math.min(90, max)
  return max
}

// How many granularity-units of history exist (earliest period → dataEnd) — drives
// which presets are offered and what the default window snaps to.
export function availableTrendUnits(periods: Period[], freq: Freq, dataEnd: string): number {
  const earliest = periods.length ? periods[periods.length - 1].from : dataEnd
  const days = Math.max(1, Math.round((Date.parse(dataEnd) - Date.parse(earliest)) / 86_400_000) + 1)
  if (freq === 'day') return days
  if (freq === 'week') return Math.ceil(days / 7)
  return Math.max(1, periods.length)
}

const isoDay = (d: Date): string => d.toISOString().slice(0, 10)

/** `iso` shifted back N days. UTC arithmetic so a DST boundary can't slide the date. */
export const isoMinusDays = (iso: string, days: number): string =>
  new Date(new Date(`${iso}T00:00:00Z`).getTime() - days * 86_400_000).toISOString().slice(0, 10)

/**
 * A trend bucket's x-axis label → the date range it covers.
 *
 * The backend labels buckets by granularity (main._DOS_BUCKET): a day is 'YYYY-MM-DD', a week
 * is the ISO year-week 'IYYY-Www', a month is 'YYYY-MM'. Clicking a point on the curve to
 * annotate it (MP-OS-136) has to turn that label back into the dates an annotation is stored
 * with, so a note dropped on week 35 covers Monday–Sunday of week 35 rather than a single day.
 *
 * ISO week 1 is the week containing 4 January, which is why the Monday is derived from that
 * date rather than from 1 January — at a year edge the two differ by up to three days.
 * Returns null for a label that doesn't parse; the caller then leaves the point unclickable
 * rather than guessing a date.
 */
export function bucketRange(name: string, freq: Freq): { from: string; to: string } | null {
  if (freq === 'day') return /^\d{4}-\d{2}-\d{2}$/.test(name) ? { from: name, to: name } : null
  if (freq === 'month') {
    const m = /^(\d{4})-(\d{2})$/.exec(name)
    if (!m) return null
    const last = new Date(Date.UTC(Number(m[1]), Number(m[2]), 0)).getUTCDate()
    return { from: `${name}-01`, to: `${name}-${String(last).padStart(2, '0')}` }
  }
  const w = /^(\d{4})-W(\d{1,2})$/.exec(name)
  if (!w) return null
  const week = Number(w[2])
  if (week < 1 || week > 53) return null
  const jan4 = new Date(Date.UTC(Number(w[1]), 0, 4))
  const mondayOfWeek1 = new Date(jan4)
  mondayOfWeek1.setUTCDate(jan4.getUTCDate() - ((jan4.getUTCDay() + 6) % 7))
  const monday = new Date(mondayOfWeek1)
  monday.setUTCDate(mondayOfWeek1.getUTCDate() + (week - 1) * 7)
  const sunday = new Date(monday)
  sunday.setUTCDate(monday.getUTCDate() + 6)
  return { from: isoDay(monday), to: isoDay(sunday) }
}

/**
 * Which plotted buckets carry a note → the text to show on the marker (MP-OS-136).
 *
 * A note is stored as a date RANGE (a campaign can run for weeks), and a bucket covers a range
 * too, so the test is overlap rather than equality — a one-day note lands on the week that
 * contains it, and a three-week campaign marks all three weekly points. Several notes on one
 * bucket are joined so the marker's hover text tells the whole story of that point.
 */
export function noteBucketMap(
  bucketNames: string[],
  freq: Freq,
  notes: { date_from: string; date_to: string; note: string }[],
): Map<string, string> {
  const out = new Map<string, string>()
  for (const name of bucketNames) {
    const r = bucketRange(name, freq)
    if (!r) continue
    // ISO dates compare lexicographically; overlap = neither range ends before the other starts.
    const hits = notes.filter((n) => n.date_from <= r.to && (n.date_to || n.date_from) >= r.from)
    if (hits.length) out.set(name, hits.map((n) => n.note).join(' · '))
  }
  return out
}

// Resolve a window + granularity + the latest data date → absolute from/to.
//
// `dataStart` is the first date the series actually covers. A window counts BACK from the
// latest data date, so a 52-week window over a series that only begins in Dec 2025
// (ingestion is capped there, MP-OS-89) resolves to a `from` that predates the data: the
// caption read "2 Sept 2025 — 31 Aug 2026" while the axis beneath it started at 2025-W51
// (Olof 18/9). Clamping here keeps the stated span and the plotted span the same thing.
// ISO dates compare lexicographically, so a string compare is the date compare.
export function windowRange(
  win: TrendWindow,
  freq: Freq,
  dataEnd: string,
  dataStart?: string,
): { from: string; to: string } {
  const clamp = (from: string): string => (dataStart && from < dataStart ? dataStart : from)
  if (win.kind === 'custom') return { from: clamp(win.from), to: win.to }
  // YTD = 1 Jan of the latest data year → the latest data date. "Hittills i år", not the
  // calendar year: a full-year window would pad the curve with empty future buckets.
  if (win.kind === 'ytd') return { from: clamp(`${dataEnd.slice(0, 4)}-01-01`), to: dataEnd }
  const end = new Date(`${dataEnd}T00:00:00Z`)
  const start = new Date(end)
  if (freq === 'day') start.setUTCDate(end.getUTCDate() - (win.n - 1))
  else if (freq === 'week') start.setUTCDate(end.getUTCDate() - (win.n * 7 - 1))
  else start.setUTCMonth(end.getUTCMonth() - (win.n - 1), 1)
  return { from: clamp(isoDay(start)), to: dataEnd }
}

// The comparison window behind the current one. Olof's reason for a cumulative curve is
// "hur ligger vi mot förra året", so a month or YTD window compares against the SAME window
// one year earlier — not the immediately preceding N months, which would put a summer
// against a spring. Day/week windows keep the equal-length preceding window, where
// "previous period" genuinely means the run-up.
export function windowPrevRange(
  range: { from: string; to: string },
  mode: 'previous' | 'year' = 'previous',
): { from: string; to: string } {
  if (mode === 'year') {
    const shift = (iso: string): string => {
      const d = new Date(`${iso}T00:00:00Z`)
      d.setUTCFullYear(d.getUTCFullYear() - 1)
      return isoDay(d)
    }
    return { from: shift(range.from), to: shift(range.to) }
  }
  const from = new Date(`${range.from}T00:00:00Z`)
  const to = new Date(`${range.to}T00:00:00Z`)
  const lenMs = to.getTime() - from.getTime()
  const prevTo = new Date(from.getTime() - 86_400_000) // the day before `from`
  const prevFrom = new Date(prevTo.getTime() - lenMs)
  return { from: isoDay(prevFrom), to: isoDay(prevTo) }
}

// Which comparison a window implies: a month/YTD span reads against last year, a day/week
// span against the run-up immediately before it.
export function prevRangeMode(win: TrendWindow, freq: Freq): 'previous' | 'year' {
  return win.kind === 'ytd' || freq === 'month' ? 'year' : 'previous'
}

// C17 — a range label must carry its years. "19 aug – 17 aug" hid that the span ran
// 2025-08-19 → 2026-08-17; any window crossing a new year was ambiguous.
export function formatRangeLabel(from: string, to: string): string {
  return formatRange(from, to)
}

const MONTHS_FULL = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)

// The selectable periods are the real months present in the dos-layer (≥15 days),
// latest first — derived from data, never a hardcoded list. So the day the store
// gains a new month, the picker shows it with zero code change; and it never offers
// an empty or future month. This is what kills the old pinned-to-May period list.
export async function fetchPeriods(): Promise<Period[]> {
  try {
    const r = await apiFetch(`${API_BASE}/api/dashboard/months`)
    if (r.status === 401) flagUnauthorized()
    if (!r.ok) return []
    const { months } = (await r.json()) as {
      months: { value: string; from: string; to: string; label: string; ongoing?: boolean; through?: string | null; settling?: boolean }[]
    }
    return months.map((m) => ({
      from: m.from,
      to: m.to,
      value: m.value,
      label: cap(m.label),
      ongoing: Boolean(m.ongoing),
      through: m.through ?? null,
      settling: Boolean(m.settling),
    }))
  } catch {
    return []
  }
}

// The period to land on: the latest COMPLETE month. An incomplete current month
// ("ongoing") is selectable but never the default, so the dashboard never opens on a
// half-month compared whole-vs-whole to a finished one. Fully dynamic — no month or
// year is assumed; it's whatever the data says.
export function defaultPeriod(periods: Period[]): Period | null {
  return periods.find((p) => !p.ongoing) ?? periods[0] ?? null
}

// A4/C15 — ONE period for the whole app. It used to live in three places (the Overview
// hero, the market view's own picker, and the SKU list), so navigating between views showed
// different months side by side and a reload silently reset to the default. Olof:
// "scorecarden, kanske man skulle lägga överst egentligen. Där du väljer och sen allting
// under följer det."
const PERIOD_KEY = 'mp_period'

/** Remember the chosen month so it survives navigation AND reload. */
export function storePeriod(value: string): void {
  try {
    localStorage.setItem(PERIOD_KEY, value)
  } catch {
    /* storage unavailable (private mode) — the selection just won't persist */
  }
}

export function readStoredPeriod(): string | null {
  try {
    return localStorage.getItem(PERIOD_KEY)
  } catch {
    return null
  }
}

/**
 * The period to open on: the remembered one when it still exists in the data, else the
 * default. A remembered month that has fallen out of the window must NOT leave the app on a
 * month with no data — it falls back rather than showing an empty dashboard.
 */
export function resolveInitialPeriod(periods: Period[], stored: string | null): Period | null {
  if (stored) {
    const hit = periods.find((p) => p.value === stored)
    if (hit) return hit
  }
  return defaultPeriod(periods)
}

// "through 25 June" — the through-date in plain English (for the ongoing-month badge).
export function throughLabel(p: Period): string {
  const m = p.through && /^(\d{4})-(\d{2})-(\d{2})$/.exec(p.through)
  return m ? `through ${Number(m[3])} ${MONTHS_FULL[Number(m[2]) - 1]}` : 'ongoing'
}

// Latest date the dosor data reaches — for the freshness note. Resolves to the through-date
// whenever the period carries one, else to the whole latest month. null when no periods are
// loaded.
//
// Heart's daily summaries lock each day the night after it closes, so the meaningful ceiling
// is the last LOCKED DAY, not "the latest month, still filling in" (MP-OS-134). A complete
// month that still carries a through-date is therefore reported by that day too.
export function dataFreshnessLabel(periods: Period[]): string | null {
  const latest = periods[0]
  if (!latest) return null
  return latest.through ? throughLabel(latest) : `through ${latest.label}`
}

// The previous *comparable* period for the period-delta (#7). For a single
// calendar month this is the previous calendar month. For any multi-month window
// there is no comparable prior period yet — reliable data starts Dec 2025 (the agreed floor,
// MIN_DATE; the business itself started Oct 2025 but pre-Dec data is sporadic), so a
// calendar-YoY would divide by autumn-2025 noise. We return null and the UI
// states "jämförbar period saknas" rather than show a misleading figure.
export function prevPeriodOf(p: Period): Period | null {
  const fm = /^(\d{4})-(\d{2})-01$/.exec(p.from)
  if (!fm || p.to.slice(0, 7) !== p.from.slice(0, 7)) return null
  let y = Number(fm[1])
  let mo = Number(fm[2]) - 1 // previous month, 1-based
  if (mo < 1) {
    mo = 12
    y -= 1
  }
  const mm = String(mo).padStart(2, '0')
  const last = new Date(y, mo, 0).getDate() // day 0 of month `mo` = last day of month `mo`
  return {
    from: `${y}-${mm}-01`,
    to: `${y}-${mm}-${String(last).padStart(2, '0')}`,
    value: `${y}-${mm}`,
    ongoing: false,
    through: null,
    settling: false, // a prior calendar month used only as the delta baseline — always settled
    label: `${cap(MONTHS_FULL[mo - 1])} ${y}`,
  }
}

export function sumSeries(chart: Chart | null): number {
  return chart?.data.reduce((s, d) => s + (Number(d.value) || 0), 0) ?? 0
}

// STG sold packages over time. dimension=time + manufacturer filter → the
// (now reliable) cube-top-500 resolve path on the backend.
export function stgTrendSpec(period: Period, freq: Freq): Spec {
  return {
    measure: 'units',
    dimension: 'time',
    // status:'__sold__' → backend expands to the ex-cancelled list so the hero
    // shares the landing's single "sold" population (matches the dos-layer).
    // No manufacturer for the "Alla märken" profile → whole-market trend.
    filters: STG_MANUFACTURER
      ? { manufacturer: STG_MANUFACTURER, status: '__sold__' }
      : { status: '__sold__' },
    time: { type: 'absolute', from: period.from, to: period.to, granularity: freq },
  }
}

// One brand's förpackningar trend (aggregate only — never SKU/raw). brand is a
// product filter; the backend resolves it over the cube's top-500 sellers.
export function brandTrendSpec(brand: string, period: Period, freq: Freq): Spec {
  return {
    measure: 'units',
    dimension: 'time',
    filters: { brand, status: '__sold__' },
    time: { type: 'absolute', from: period.from, to: period.to, granularity: freq },
  }
}

// Customers over time (#5) — customers is now a first-class measure. Weekly grain
// so a single month renders as a real curve (per-week active customers), not one dot.
export function customersTrendSpec(from: string, to: string, freq: Freq = 'week'): Spec {
  return {
    measure: 'customers',
    dimension: 'time',
    // Scope to the supplier (its SKU set, resolved server-side via the complete
    // sku_meta map) so the curve is this supplier's customers — not the whole market.
    // No manufacturer → "Alla märken" whole-market profile.
    filters: STG_MANUFACTURER
      ? { manufacturer: STG_MANUFACTURER, status: '__sold__' }
      : { status: '__sold__' },
    time: { type: 'absolute', from, to, granularity: freq },
  }
}

// Merge several brands' trends into one multi-series line Chart keyed by period.
export function mergeTrends(named: { name: string; chart: Chart | null }[]): Chart {
  const periods = new Set<string>()
  named.forEach((n) => n.chart?.data.forEach((d) => periods.add(String(d.name))))
  const order = [...periods].sort()
  const data = order.map((p) => {
    const row: ChartDatum = { name: p }
    const reference = named.map((n) => n.chart?.data.find((d) => String(d.name) === p)).find(Boolean)
    for (const key of ['period_from', 'period_to', 'period_kind']) {
      if (reference?.[key] != null) row[key] = reference[key]
    }
    // MP-OS-207: the row is incomplete when any merged series is (the day coverage is shared).
    const incomplete = named.map((n) => n.chart?.data.find((d) => String(d.name) === p)).filter(isIncompletePoint)
    if (incomplete.length) {
      row.coverage = 'incomplete'
      row.missing_days = Math.max(...incomplete.map(missingDays))
    }
    named.forEach((n) => {
      const pt = n.chart?.data.find((d) => String(d.name) === p)
      const sameWindow = !pt || ['period_from', 'period_to'].every((key) => pt[key] === reference?.[key])
      row[n.name] = sameWindow ? measurement(pt?.value) : null
    })
    return row
  })
  // MPD-133 M6: each curve keeps its own attribute coverage, so a warning names the curve it is for.
  const withCoverage = named.filter((n) => n.chart?.attribute_coverage)
  return {
    type: 'line',
    unit: named.find((n) => n.chart)?.chart?.unit ?? 'CU',
    ref: null,
    series: named.map((n) => n.name),
    data,
    ...(withCoverage.length
      ? { series_attribute_coverage: Object.fromEntries(withCoverage.map((n) => [n.name, n.chart!.attribute_coverage!])) }
      : {}),
  }
}

export interface Assortment { segments: string[]; leaves: string[] }
export async function fetchAssortment(): Promise<Assortment> {
  const q = new URLSearchParams()
  if (TENANT.manufacturer) q.set('manufacturer', TENANT.manufacturer)
  const r = await apiFetch(`${API_BASE}/api/dashboard/assortment?${q}`)
  if (r.status === 401) flagUnauthorized()
  if (!r.ok) throw new Error('Could not load supplier assortment.')
  return r.json()
}

export interface ToplistTrend {
  cu: Chart; share: Chart; from: string; to: string
  coverage: { first: string | null; last: string | null }; source: string; note: string
}
export async function fetchToplistTrend(opts: {
  from: string; to: string; sku?: string; brand?: string; segment?: string; area?: string; leaf?: string
}): Promise<ToplistTrend> {
  const q = new URLSearchParams({ date_from: opts.from, date_to: opts.to, granularity: 'week' })
  if (TENANT.manufacturer) q.set('manufacturer', TENANT.manufacturer)
  for (const key of ['sku', 'brand', 'segment', 'area', 'leaf'] as const) if (opts[key]) q.set(key, opts[key]!)
  const r = await apiFetch(`${API_BASE}/api/dashboard/toplist-trend?${q}`)
  if (r.status === 401) flagUnauthorized()
  if (!r.ok) throw new Error('Could not load the curve.')
  return r.json()
}
